import { useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { TRPCInputs, useTRPC } from '~/utils/trpc'

type Props = TRPCInputs['orgMembers']['listTopicSubscriberMembers']

// TODO: make this a provider hook to share state across multiple components if needed
const useTopicSubscribersState = (props: Props) => {
  const trpc = useTRPC()

  const [subscribedMembers, setSubscribedMembers] = useState<string[]>([])

  const { data, isLoading } = useQuery(trpc.orgMembers.listTopicSubscriberMembers.queryOptions(props))

  const initialSubscribers = useMemo(() => {
    return data?.map((m) => m.id) || []
  }, [data])

  useEffect(() => {
    if (!isLoading && subscribedMembers.length === 0 && initialSubscribers.length > 0) {
      setSubscribedMembers(initialSubscribers)
    }
  }, [isLoading, initialSubscribers])

  const isDirty = useMemo(() => {
    return !_.isEqual(subscribedMembers.sort(), initialSubscribers.sort())
  }, [subscribedMembers, initialSubscribers])

  const operations = useMemo(() => {
    const toAdd = subscribedMembers
      .filter((i) => !initialSubscribers.includes(i))
      .map((memberID) => ({
        memberID,
        operation: 'subscribe' as const
      }))
    const toRemove = initialSubscribers
      .filter((i) => !subscribedMembers.includes(i))
      .map((memberID) => ({
        memberID,
        operation: 'unsubscribe' as const
      }))
    return [...toAdd, ...toRemove] satisfies { operation: 'subscribe' | 'unsubscribe'; memberID: string }[]
  }, [subscribedMembers, initialSubscribers])

  return {
    setSubscribedMembers,
    initialSubscribers,
    subscribedMembers,
    operations,
    isLoading,
    isDirty
  }
}

export { useTopicSubscribersState }
